import { InputAdornment, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import DeleteIcon from "@material-ui/icons/Delete";
import Checkbox from "@saleor/components/Checkbox";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import TableHead from "@saleor/components/TableHead";
import { SaleVariantDetails_sale_productVariants } from "@saleor/discounts/types/SaleVariantDetails";
// import TableCellAvatar from "@saleor/components/TableCellAvatar";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { buttonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SearchCategories_search_edges_node } from "@saleor/searches/types/SearchCategories";
// import { maybe } from "@saleor/misc";
import {
  SearchProducts_search_edges_node,
  SearchProducts_search_edges_node_variants
} from "@saleor/searches/types/SearchProducts";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import {
  DiscountValueTypeEnum,
  SaleCatalogueInput
} from "@saleor/types/globalTypes";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { FormattedMessage, useIntl } from "react-intl";

export interface FormData {
  products: SearchProducts_search_edges_node[];
  query: string;
  onBrandChange(event: React.ChangeEvent<any>);
}

const useStyles = makeStyles(
  theme => ({
    allCheckbox: {
      width: 100
    },
    allFields: {
      marginLeft: "10px",
      marginRight: "10px"
    },
    allSpan: {
      textAlign: "center"
    },
    allSubsidy: {
      width: "80%"
    },
    allSusidyMain: {
      display: "flex",
      justifyContent: "center"
    },
    allText: {
      fontSize: "1rem",
      marginTop: "10px",
      width: "20%"
    },
    avatar: {
      "&&:first-child": {
        paddingLeft: 0
      },
      width: 72
    },
    buttonAction: {
      display: "flex",
      justifyContent: "center"
    },
    checkboxCell: {
      paddingLeft: 0,
      width: 88
    },
    colActions: {
      "&:last-child": {
        paddingRight: 0
      },
      width: 76 + theme.spacing(0.5)
    },
    colName: {
      paddingLeft: 0
    },
    colPrice: {
      textAlign: "center",
      width: "20%"
    },

    content: {
      overflowY: "hidden"
    },
    dialog: {
      height: "calc(100% - 64px)",
      maxHeight: 700
    },
    loadMoreLoaderContainer: {
      alignItems: "center",
      display: "flex",
      height: theme.spacing(3),
      justifyContent: "center",
      marginTop: theme.spacing(3)
    },
    note: {
      fontStyle: "italic"
    },
    scrollArea: {
      overflowY: "scroll"
    },
    searchBox: {
      display: "flex",
      marginBottom: "10px",
      paddingLeft: "10px",
      paddingRight: "20px"
    },
    selectAll: {
      display: "flex"
    },
    selectallcount: {
      color: theme.palette.primary.main
    },
    tabs: {
      alignItems: "center",
      display: "flex",
      marginBottom: "10px"
    }
  }),
  { name: "AssignDiscountProductDialog" }
);

const useScrollableStyles = makeStyles(
  theme => ({
    content: {
      overflowY: "hidden"
    },
    dialog: {
      height: "calc(100% - 64px)",
      maxHeight: 700,
      maxWidth: "calc(100% - 64px)"
    },
    loadMoreLoaderContainer: {
      alignItems: "center",
      display: "flex",
      height: theme.spacing(3),
      justifyContent: "center",
      marginTop: theme.spacing(3)
    },
    scrollArea: {
      overflowY: "scroll"
    }
  }),
  { name: "AssignDiscountProductDialog" }
);

export interface ProductSubsidyItem {
  id: string;
  name: string;
  retailPrice: string;
  sellingPrice: string;
  tenantSubsidy: string;
  smoSubsidy: string;
}

export interface ProductSubsidyErrorItem {
  id: string;
  name: string;
  retailPrice: string;
  sellingPrice: string | null;
  tenantSubsidy: string | null;
  smoSubsidy: string | null;
}

export interface ProductVariantSelection {
  id: string;
  productName: string;
  name: string;
  price: number | null;
  sellingPrice: string;
  tenantSubsidy: string;
  smoSubsidy: string;
}

export interface AssignDiscountProductDialogProps extends FetchMoreProps {
  brands: any;
  categories: SearchCategories_search_edges_node[];
  confirmButtonState: ConfirmButtonTransitionState;
  open: boolean;
  products: SearchProducts_search_edges_node[];
  loading: boolean;
  type: DiscountValueTypeEnum;
  onClose: () => void;
  onFetch: (value: string) => void;
  onSubmit: (data: SaleCatalogueInput) => void;
  onSelectBrand: (value: string) => void;
  fetchBrands?: (query: string) => void;
  fetchMoreBrands?: FetchMoreProps;
  selectCategory: (value) => void;
  fetchCategories: (query: string) => void;
  fetchMoreCategories: FetchMoreProps;
  variants: SaleVariantDetails_sale_productVariants;
  onFetchMoreVariants: () => void;
}

function handleProductAssign(
  variant: SearchProducts_search_edges_node_variants,
  product: SearchProducts_search_edges_node,
  isSelected: boolean,
  selectedProducts: ProductVariantSelection[],
  setSelectedProducts: (data: ProductVariantSelection[]) => void
) {
  if (isSelected) {
    setSelectedProducts(
      selectedProducts.filter(
        selectedProduct => selectedProduct.id !== variant.id
      )
    );
  } else {
    setSelectedProducts([
      ...selectedProducts,
      {
        id: variant.id,
        name: variant.sku,
        price: variant.price.amount,
        productName: product.name,
        sellingPrice: "0",
        smoSubsidy: "0",
        tenantSubsidy: "0"
      }
    ]);
  }
}

function handleSelectAll(
  products: SearchProducts_search_edges_node[],
  setSelectedProducts: (data: ProductVariantSelection[]) => void
) {
  const getAll = [];
  products.forEach(product => {
    product.variants.forEach(variant => {
      if (
        !getAll.some(selectedVariants => selectedVariants.id === variant.id)
      ) {
        getAll.push({
          id: variant.id,
          name: variant.sku,
          price: variant.price.amount,
          productName: product.name,
          sellingPrice: "0",
          smoSubsidy: "0",
          tenantSubsidy: "0"
        });
      }
    });
  });
  setSelectedProducts(getAll);
}

function handlePreloadVariants(
  variants: SaleVariantDetails_sale_productVariants,
  selectedProducts: ProductVariantSelection[],
  setSelectedProducts: (data: ProductVariantSelection[]) => void,
  onLoadMore: () => void
) {
  const preloadSelection = variants.edges.map(variant => ({
    id: variant.node.id,
    name: variant.node.sku,
    price: variant.node.price.amount,
    productName: variant.node.product.name,
    sellingPrice: `${variant.node.sellingPrice.amount}`,
    smoSubsidy: variant.node.smoShare,
    tenantSubsidy: variant.node.tenantShare
  }));
  setSelectedProducts([...selectedProducts, ...preloadSelection]);

  if (variants.pageInfo.hasNextPage) {
    onLoadMore();
  }
}

const AssignProductSubsidyDialog: React.FC<AssignDiscountProductDialogProps> = props => {
  const {
    // brands,
    categories,
    confirmButtonState,
    hasMore,
    open,
    loading,
    products,
    type,
    onClose,
    onFetch,
    onFetchMore,
    onSubmit,
    // onSelectBrand,
    // fetchBrands,
    // fetchMoreBrands,
    selectCategory,
    fetchCategories,
    fetchMoreCategories,
    variants,
    onFetchMoreVariants
  } = props;
  const classes = useStyles(props);
  const scrollableClassess = useScrollableStyles(props);
  const scrollableDialogClasses = useScrollableDialogStyle({});

  const intl = useIntl();
  const [query, onQueryChange] = useSearchQuery(onFetch);
  const [selectedProducts, setSelectedProducts] = React.useState<
    ProductVariantSelection[]
  >([]);
  const [shareErrors, setShareErrors] = React.useState<
    ProductSubsidyErrorItem[]
  >([]);
  const [subsidyData, setSubsidyData] = React.useState<ProductSubsidyItem[]>(
    []
  );
  const container = React.useRef<HTMLDivElement>();
  const [step, setStep] = React.useState("SelectProducts");
  const [isSelectAll, setSelectAll] = React.useState(false);
  const [isLoadingAll, setIsLoadingAll] = React.useState(false);
  const [forAllSubsidy, setForAllSubsidy] = React.useState(false);
  const [allSellingPriceValue, setAllSellingPriceValue] = React.useState("0");
  const [allTenantValue, setAllTenantValue] = React.useState("0");
  const [allSMOValue, setAllSMOValue] = React.useState("0");
  const [internalSearchValue, setInternalSearchValue] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setSelectedProducts([]);
      setSubsidyData([]);
      setShareErrors([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (variants && open) {
      handlePreloadVariants(
        variants,
        selectedProducts,
        setSelectedProducts,
        onFetchMoreVariants
      );
    }
  }, [variants, open]);

  React.useEffect(() => {
    if (step === "SelectProducts") {
      setShareErrors([]);
    }
  }, [step]);

  React.useEffect(() => {
    if (step === "DiscountSubsidy" && isSelectAll) {
      setIsLoadingAll(true);
      if (hasMore) {
        onFetchMore();
      } else {
        handleSelectAll(products, setSelectedProducts);
        setIsLoadingAll(false);
      }
    } else if (step === "SelectProducts" && isSelectAll) {
      handleSelectAll(products, setSelectedProducts);
    }
  }, [step, hasMore, products]);

  React.useEffect(() => {
    const newArray: ProductSubsidyItem[] = [];
    selectedProducts.forEach(product => {
      const findSubsidy = subsidyData.filter(
        subsidy => subsidy.id === product.id
      );
      if (findSubsidy.length === 0) {
        const thisProduct: ProductSubsidyItem = {
          id: product.id,
          name: `${product.productName} (${product.name})`,
          retailPrice: `${product.price}`,
          sellingPrice: product.sellingPrice,
          smoSubsidy: product.smoSubsidy,
          tenantSubsidy: product.tenantSubsidy
        };

        newArray.push(thisProduct);
      }
    });
    setSubsidyData([
      ...newArray,
      ...subsidyData.filter(({ id }) =>
        selectedProducts.map(({ id }) => id).includes(id)
      )
    ]);
  }, [selectedProducts]);

  // const handleSubmit = () => onSubmit(selectedProducts);
  const handleSubmit = () => {
    setShareErrors([]);
    const errors = [];

    if (forAllSubsidy) {
      const allReturn = subsidyData.map(item => ({
        product_variant: item.id,
        sellingPrice:
          type === DiscountValueTypeEnum.PERCENTAGE
            ? null
            : allSellingPriceValue,
        smoShare: allSMOValue,
        tenantShare: allTenantValue
      }));
      onSubmit({ productVariants: allReturn });
    }

    if (type === DiscountValueTypeEnum.PERCENTAGE) {
      subsidyData.forEach(item => {
        let getErrors = {
          ...item,
          sellingPrice: null,
          smoSubsidy: null,
          tenantSubsidy: null
        };
        let insertError = false;
        if (
          parseFloat(item.smoSubsidy) + parseFloat(item.tenantSubsidy) !==
          100
        ) {
          insertError = true;
          getErrors = {
            ...getErrors,
            smoSubsidy: "Tenant and SMOP share should be equal to 100%",
            tenantSubsidy: "Tenant and SMOP share should be equal to 100%"
          };

          if (insertError) {
            errors.push(getErrors);
          }
        }
      });
      if (errors.length > 0) {
        setShareErrors(errors);
        return;
      }
    } else {
      subsidyData.forEach(item => {
        let getErrors = {
          ...item,
          sellingPrice: null,
          smoSubsidy: null,
          tenantSubsidy: null
        };
        let insertError = false;
        const subsidyTotal = parseFloat(
          `${parseFloat(item.smoSubsidy) + parseFloat(item.tenantSubsidy)}`
        ).toFixed(2);
        const sellingDiscount = parseFloat(
          `${parseFloat(item.retailPrice) - parseFloat(item.sellingPrice)}`
        ).toFixed(2);

        if (subsidyTotal !== sellingDiscount) {
          insertError = true;
          getErrors = {
            ...getErrors,
            smoSubsidy: "Tenant and SMOP share should be equal to Subsidy",
            tenantSubsidy: "Tenant and SMOP share should be equal to Subsidy"
          };
          // errors.push(errors.push(
          // {...item,
          //   tenantSubsidy: "Tenant and SMOP share should be equal to Selling Price",
          //   smoSubsidy: "Tenant and SMOP share should be equal to Selling Price"}
          // );
        }

        if (parseFloat(item.retailPrice) < parseFloat(item.sellingPrice)) {
          insertError = true;
          getErrors = {
            ...getErrors,
            sellingPrice:
              "Selling Price should not be greater than Retail Price."
          };
        }

        if (insertError) {
          errors.push(getErrors);
        }
      });
      if (errors.length > 0) {
        setShareErrors(errors);
        return;
      }
    }

    const newReturn = subsidyData.map(item => ({
      product_variant: item.id,
      sellingPrice:
        type === DiscountValueTypeEnum.PERCENTAGE ? null : item.sellingPrice,
      smoShare: item.smoSubsidy,
      tenantShare: item.tenantSubsidy
    }));
    onSubmit({ productVariants: newReturn });
  };

  const containerHeight = container.current?.scrollHeight - 250;
  // const containerHeight = 566 - 250;
  const [sellerDisplayName, setSellerDisplayName] = useStateFromProps("");
  const initialForm = {
    id: null,
    name: "",
    sellerBrand: sellerDisplayName
  };

  // const sellerBrandChoices = maybe(
  //   () =>
  //     brands.map(brand => ({
  //       label: brand?.name + " - " + brand.legalName,
  //       value: brand?.id
  //     })),
  //   []
  // );

  const categoryChoices = maybe(
    () =>
      categories.map(category => ({
        label: category?.name,
        value: category?.id
      })),
    []
  );

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data: formData, change, submit }) => {
        // const handleSelectBrand = createSingleAutocompleteSelectHandler(
        //   change,
        //   setSellerDisplayName,
        //   sellerBrandChoices
        // );

        const handleSelectCategories = createSingleAutocompleteSelectHandler(
          change,
          setSellerDisplayName,
          categoryChoices
        );

        const handleSellingPriceSubsidyChange = e => {
          if (!/^[.0-9\b]+$/.test(e.target.value)) {
            return;
          }

          if (e.target.name === "allSellingPrice") {
            setAllSellingPriceValue(e.target.value);
            return;
          }

          const newArray: ProductSubsidyItem[] = [];
          subsidyData.forEach(product => {
            if (e.target.name === product.id) {
              const thisProduct: ProductSubsidyItem = {
                id: product.id,
                name: product.name,
                retailPrice: product.retailPrice,
                sellingPrice: e.target.value,
                smoSubsidy: product.smoSubsidy,
                tenantSubsidy: product.tenantSubsidy
              };
              newArray.push(thisProduct);
            } else {
              const thisProduct: ProductSubsidyItem = {
                id: product.id,
                name: product.name,
                retailPrice: product.retailPrice,
                sellingPrice: product.sellingPrice,
                smoSubsidy: product.smoSubsidy,
                tenantSubsidy: product.tenantSubsidy
              };
              newArray.push(thisProduct);
            }
          });
          setSubsidyData(newArray);
        };

        const handleTenantSubsidyChange = e => {
          if (!/^[.0-9\b]+$/.test(e.target.value)) {
            return;
          }

          if (e.target.name === "allTenant") {
            setAllTenantValue(e.target.value);
            return;
          }

          const newArray: ProductSubsidyItem[] = [];
          subsidyData.forEach(product => {
            if (e.target.name === product.id) {
              const thisProduct: ProductSubsidyItem = {
                id: product.id,
                name: product.name,
                retailPrice: product.retailPrice,
                sellingPrice: product.sellingPrice,
                smoSubsidy: product.smoSubsidy,
                tenantSubsidy: e.target.value
              };
              newArray.push(thisProduct);
            } else {
              const thisProduct: ProductSubsidyItem = {
                id: product.id,
                name: product.name,
                retailPrice: product.retailPrice,
                sellingPrice: product.sellingPrice,
                smoSubsidy: product.smoSubsidy,
                tenantSubsidy: product.tenantSubsidy
              };
              newArray.push(thisProduct);
            }
          });
          setSubsidyData(newArray);
        };

        const handleSMOSubsidyChange = e => {
          if (!/^[.0-9\b]+$/.test(e.target.value)) {
            return;
          }

          if (e.target.name === "allSMO") {
            setAllSMOValue(e.target.value);
            return;
          }

          const newArray: ProductSubsidyItem[] = [];
          subsidyData.forEach(product => {
            if (e.target.name === product.id) {
              const thisProduct: ProductSubsidyItem = {
                id: product.id,
                name: product.name,
                retailPrice: product.retailPrice,
                sellingPrice: product.sellingPrice,
                smoSubsidy: e.target.value,
                tenantSubsidy: product.tenantSubsidy
              };
              newArray.push(thisProduct);
            } else {
              const thisProduct: ProductSubsidyItem = {
                id: product.id,
                name: product.name,
                retailPrice: product.retailPrice,
                sellingPrice: product.sellingPrice,
                smoSubsidy: product.smoSubsidy,
                tenantSubsidy: product.tenantSubsidy
              };
              newArray.push(thisProduct);
            }
          });
          setSubsidyData(newArray);
        };

        const handleRemove = id => {
          const filterRemove = subsidyData.filter(item => item.id !== id);
          const filterSelected = selectedProducts.filter(
            item => item.id !== id
          );
          setSelectedProducts(filterSelected);
          setSubsidyData(filterRemove);
          if (filterSelected.length === 0) {
            setStep("SelectProducts");
          }
        };

        let btnDisabled = false;
        if (!formData.sellerBrand) {
          btnDisabled = true;
        } else if (formData.sellerBrand && products && products.length) {
          btnDisabled = false;
        } else if (!formData.sellerBrand && products && products.length) {
          btnDisabled = false;
        } else {
          btnDisabled = true;
        }
        return (
          <Dialog
            onClose={onClose}
            open={open}
            classes={{ paper: scrollableClassess.dialog }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <FormattedMessage
                defaultMessage="Assign Product"
                description="dialog header"
              />
            </DialogTitle>
            <DialogContent
              className={scrollableDialogClasses.content}
              ref={container}
            >
              <div className={classes.tabs}>
                <Button
                  id="selectProductBtn"
                  color={step === "SelectProducts" ? "primary" : "default"}
                >
                  <FormattedMessage {...buttonMessages.selectProducts} />
                </Button>
                <span>{`>`}</span>
                <Button
                  id="percentageDiscountBtn"
                  color={step === "DiscountSubsidy" ? "primary" : "default"}
                  disabled={step === "SelectProducts"}
                >
                  {type === DiscountValueTypeEnum.PERCENTAGE ? (
                    <FormattedMessage {...buttonMessages.percentageDiscount} />
                  ) : (
                    <FormattedMessage {...buttonMessages.saleDiscount} />
                  )}
                </Button>
              </div>
              {step === "SelectProducts" ? (
                <>
                  {/* <SingleAutocompleteSelectField
                    label={intl.formatMessage({
                      defaultMessage: "Brand:"
                    })}
                    name="sellerBrand"
                    displayValue={sellerDisplayName}
                    choices={sellerBrandChoices}
                    disabled={false}
                    value={formData.sellerBrand}
                    onChange={e => {
                      handleSelectBrand(e);
                      onSelectBrand(e.target.value);
                    }}
                    fetchChoices={fetchBrands}
                    {...fetchMoreBrands}
                  /> */}
                  <SingleAutocompleteSelectField
                    idInputTxt="categoriesTxt"
                    label={intl.formatMessage({
                      defaultMessage: "Categories:"
                    })}
                    name="sellerBrand"
                    displayValue={sellerDisplayName}
                    choices={categoryChoices}
                    disabled={false}
                    value={formData.sellerBrand}
                    onChange={e => {
                      handleSelectCategories(e);
                      selectCategory(e.target.value);
                    }}
                    fetchChoices={fetchCategories}
                    {...fetchMoreCategories}
                  />
                  <FormSpacer />
                  <TextField
                    disabled={!formData.sellerBrand ? true : false}
                    id="searchProductsTxt"
                    name="query"
                    value={query}
                    onChange={onQueryChange}
                    label={intl.formatMessage({
                      defaultMessage: "Search Products"
                    })}
                    placeholder={intl.formatMessage({
                      defaultMessage:
                        "Search by product name, attribute, product type etc..."
                    })}
                    fullWidth
                    InputProps={{
                      autoComplete: "off",
                      endAdornment: loading && <CircularProgress size={16} />
                    }}
                  />
                  {formData.sellerBrand ? (
                    <>
                      <FormSpacer />
                      {open && (
                        <div
                          className={scrollableDialogClasses.scrollArea}
                          style={{ height: containerHeight }}
                        >
                          <InfiniteScroll
                            pageStart={0}
                            loadMore={onFetchMore}
                            hasMore={hasMore}
                            useWindow={false}
                            loader={
                              <div
                                className={
                                  scrollableDialogClasses.loadMoreLoaderContainer
                                }
                                key={0}
                              >
                                <CircularProgress size={16} />
                              </div>
                            }
                            threshold={10}
                          >
                            <ResponsiveTable key="table">
                              <TableBody>
                                <TableRow key="all">
                                  <TableCell
                                    className={classes.colName}
                                    id="selectAllValue"
                                  >
                                    Select All
                                  </TableCell>
                                  <TableCell
                                    className={classes.colPrice}
                                  ></TableCell>
                                  <TableCell
                                    className={classes.colPrice}
                                  ></TableCell>
                                  <TableCell
                                    padding="checkbox"
                                    className={classes.checkboxCell}
                                  >
                                    <Checkbox
                                      id="selectAllCheckbox"
                                      checked={isSelectAll}
                                      onChange={e => {
                                        setSelectAll(!isSelectAll);
                                        if (e.target.checked) {
                                          handleSelectAll(
                                            products,
                                            setSelectedProducts
                                          );
                                        } else {
                                          setSelectedProducts([]);
                                        }
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                                {products &&
                                  products.map(product =>
                                    product.variants.map((variant, index) => {
                                      const isSelected = selectedProducts.some(
                                        selectedProduct =>
                                          selectedProduct.id === variant.id
                                      );

                                      return (
                                        <TableRow key={variant.id}>
                                          {/* <TableCellAvatar
                                        className={classes.avatar}
                                        thumbnail={maybe(() => product.thumbnail.url)}
                                      /> */}
                                          <TableCell
                                            className={classes.colName}
                                            id={`productNameRow${index}`}
                                          >
                                            <div>{product.name}</div>
                                          </TableCell>
                                          <TableCell
                                            id={`skuRow${index}`}
                                            className={classes.colPrice}
                                          >
                                            {variant.sku}
                                          </TableCell>
                                          <TableCell
                                            className={classes.colPrice}
                                            id={`variantNameRow${index}`}
                                          >
                                            {variant.name}
                                          </TableCell>
                                          <TableCell
                                            id={`checkboxRow${index}`}
                                            padding="checkbox"
                                            className={classes.checkboxCell}
                                          >
                                            <Checkbox
                                              checked={isSelected}
                                              onChange={e => {
                                                if (!e.target.checked) {
                                                  setSelectAll(false);
                                                }
                                                handleProductAssign(
                                                  variant,
                                                  product,
                                                  isSelected,
                                                  selectedProducts,
                                                  setSelectedProducts
                                                );
                                              }}
                                            />
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })
                                  )}
                                {products && products.length
                                  ? ""
                                  : "No Products Found"}
                              </TableBody>
                            </ResponsiveTable>
                          </InfiniteScroll>
                        </div>
                      )}
                    </>
                  ) : (
                    ""
                  )}
                </>
              ) : (
                <>
                  <FormSpacer />
                  <div className={classes.searchBox}>
                    <TextField
                      fullWidth
                      id="searchProductSubsidyTxt"
                      name="internalSearch"
                      label="Search Product"
                      value={internalSearchValue}
                      onChange={e => setInternalSearchValue(e.target.value)}
                      // InputProps={{
                      //   endAdornment: (
                      //     <InputAdornment position="end">
                      //       <Typography
                      //         component="span"
                      //         variant="caption"
                      //       >
                      //         PHP
                      //       </Typography>
                      //     </InputAdornment>
                      //   )
                      // }}
                    />
                  </div>
                  <div className={classes.selectAll}>
                    <div className={classes.allCheckbox}>
                      <Checkbox
                        id="selectAllSubsidyCheckbox"
                        checked={forAllSubsidy}
                        disabled={isLoadingAll}
                        onChange={() => setForAllSubsidy(!forAllSubsidy)}
                      />
                    </div>
                    <div className={classes.allText}>
                      <div>
                        <span>Select All</span>
                      </div>
                      {forAllSubsidy && (
                        <div className={classes.selectallcount}>
                          <span>{`${selectedProducts.length} products selected`}</span>
                        </div>
                      )}
                    </div>
                    <div className={classes.allSubsidy}>
                      <div className={classes.allSusidyMain}>
                        {type === DiscountValueTypeEnum.ABSOLUTE_VALUE && (
                          <div className={classes.allFields}>
                            <TextField
                              id="allProductSellingPriceTxt"
                              name={"allSellingPrice"}
                              label="Selling Price"
                              value={allSellingPriceValue}
                              disabled={isLoadingAll || !forAllSubsidy}
                              onChange={handleSellingPriceSubsidyChange}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Typography
                                      component="span"
                                      variant="caption"
                                    >
                                      PHP
                                    </Typography>
                                  </InputAdornment>
                                )
                              }}
                            />
                          </div>
                        )}
                        <div className={classes.allFields}>
                          <TextField
                            disabled={isLoadingAll || !forAllSubsidy}
                            id="allTenantSubsidyTxt"
                            name="allTenant"
                            label="Tenant"
                            value={allTenantValue}
                            onChange={handleTenantSubsidyChange}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography
                                    component="span"
                                    variant="caption"
                                  >
                                    {type ===
                                    DiscountValueTypeEnum.ABSOLUTE_VALUE
                                      ? "PHP"
                                      : "%"}
                                  </Typography>
                                </InputAdornment>
                              )
                            }}
                          />
                        </div>
                        <div className={classes.allFields}>
                          <TextField
                            disabled={isLoadingAll || !forAllSubsidy}
                            id="allSMOSubsidyTxt"
                            name="allSMO"
                            label="SMO"
                            value={allSMOValue}
                            onChange={handleSMOSubsidyChange}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography
                                    component="span"
                                    variant="caption"
                                  >
                                    {type ===
                                    DiscountValueTypeEnum.ABSOLUTE_VALUE
                                      ? "PHP"
                                      : "%"}
                                  </Typography>
                                </InputAdornment>
                              )
                            }}
                          />
                        </div>
                      </div>
                      <div className={classes.allSpan}>
                        <span>
                          {`${
                            type === DiscountValueTypeEnum.PERCENTAGE
                              ? "Percentage"
                              : "Absolute"
                          } values will apply to all selected products`}{" "}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    className={scrollableDialogClasses.scrollArea}
                    style={{ height: containerHeight }}
                  >
                    {isLoadingAll ? (
                      <div className={classes.allSusidyMain}>
                        <CircularProgress size={16} />
                      </div>
                    ) : (
                      <ResponsiveTable key="table">
                        <TableHead
                          colSpan={
                            type === DiscountValueTypeEnum.PERCENTAGE ? 5 : 6
                          }
                          disabled={false}
                          items={maybe(() => subsidyData.map(edge => edge))}
                          hideCheckBox
                        >
                          <TableCell className={classes.colName}>
                            <span>
                              <FormattedMessage defaultMessage="Product Name" />
                            </span>
                          </TableCell>
                          <TableCell className={classes.colPrice}>
                            <FormattedMessage defaultMessage="Retail Price" />
                          </TableCell>
                          {type === DiscountValueTypeEnum.ABSOLUTE_VALUE && (
                            <TableCell className={classes.colPrice}>
                              <FormattedMessage defaultMessage="Selling Price" />
                            </TableCell>
                          )}
                          <TableCell className={classes.colPrice}>
                            <FormattedMessage defaultMessage="Tenant Discount" />
                          </TableCell>
                          <TableCell className={classes.colPrice}>
                            <FormattedMessage defaultMessage="SMO Discount" />
                          </TableCell>
                          <TableCell className={classes.colActions} />
                        </TableHead>
                        <TableBody>
                          {subsidyData &&
                            subsidyData
                              .filter(({ name }) =>
                                name.includes(internalSearchValue)
                              )
                              .map((product, index) => {
                                const hasErrors = shareErrors.some(
                                  selectedProduct =>
                                    selectedProduct.id === product.id
                                );

                                let sellingPriceError = null;
                                let tenantError = null;
                                if (hasErrors) {
                                  sellingPriceError = shareErrors.filter(
                                    error => error.id === product.id
                                  )[0].sellingPrice;
                                  tenantError = shareErrors.filter(
                                    error => error.id === product.id
                                  )[0].tenantSubsidy;
                                }

                                return (
                                  <TableRow key={product.id}>
                                    {/* <TableCellAvatar
                                    className={classes.avatar}
                                    thumbnail={maybe(() => product.thumbnail.url)}
                                  /> */}
                                    <TableCell
                                      className={classes.colName}
                                      id={`assignProductNameRow${index}`}
                                    >
                                      {product.name}
                                    </TableCell>
                                    <TableCell
                                      className={classes.colPrice}
                                      id={`assignProductPriceRow${index}`}
                                    >
                                      {product.retailPrice}
                                    </TableCell>
                                    {type ===
                                      DiscountValueTypeEnum.ABSOLUTE_VALUE && (
                                      <TableCell className={classes.colPrice}>
                                        <TextField
                                          name={product.id}
                                          id={`assignProductSellingTxtRow${index}`}
                                          label="Selling Price"
                                          fullWidth
                                          error={sellingPriceError}
                                          helperText={sellingPriceError}
                                          value={product.sellingPrice}
                                          disabled={forAllSubsidy}
                                          onChange={
                                            handleSellingPriceSubsidyChange
                                          }
                                          InputProps={{
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                <Typography
                                                  component="span"
                                                  variant="caption"
                                                >
                                                  PHP
                                                </Typography>
                                              </InputAdornment>
                                            )
                                          }}
                                        />
                                      </TableCell>
                                    )}
                                    <TableCell className={classes.colPrice}>
                                      <TextField
                                        name={product.id}
                                        label="Tenant"
                                        id={`assignProductTenantSubsidyTxtRow${index}`}
                                        fullWidth
                                        error={tenantError}
                                        helperText={tenantError}
                                        value={product.tenantSubsidy}
                                        onChange={handleTenantSubsidyChange}
                                        disabled={forAllSubsidy}
                                        InputProps={{
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              <Typography
                                                component="span"
                                                variant="caption"
                                              >
                                                {type ===
                                                DiscountValueTypeEnum.ABSOLUTE_VALUE
                                                  ? "PHP"
                                                  : "%"}
                                              </Typography>
                                            </InputAdornment>
                                          )
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell className={classes.colPrice}>
                                      <TextField
                                        name={product.id}
                                        label="SMO"
                                        id={`assignProductSMOSubsidyTxtRow${index}`}
                                        fullWidth
                                        error={tenantError}
                                        value={product.smoSubsidy}
                                        onChange={handleSMOSubsidyChange}
                                        disabled={forAllSubsidy}
                                        InputProps={{
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              <Typography
                                                component="span"
                                                variant="caption"
                                              >
                                                {type ===
                                                DiscountValueTypeEnum.ABSOLUTE_VALUE
                                                  ? "PHP"
                                                  : "%"}
                                              </Typography>
                                            </InputAdornment>
                                          )
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell className={classes.colActions}>
                                      {/* <Button className={classes.buttonAction}> */}
                                      <DeleteIcon
                                        id={`assignProductDeleteBtnRow${index}`}
                                        onClick={() => handleRemove(product.id)}
                                      />
                                      {/* </Button> */}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          {products && products.length
                            ? ""
                            : "No Products Found"}
                        </TableBody>
                      </ResponsiveTable>
                    )}
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              {step === "SelectProducts" ? (
                <Button onClick={onClose} disabled={isLoadingAll} id="backBtn">
                  <FormattedMessage {...buttonMessages.back} />
                </Button>
              ) : (
                <Button
                  onClick={() => setStep("SelectProducts")}
                  disabled={isLoadingAll}
                  id="backToSelectProductsBtn"
                >
                  <FormattedMessage {...buttonMessages.back} />
                </Button>
              )}

              {step === "SelectProducts" ? (
                <ConfirmButton
                  idConfirmButton="next"
                  disabled={
                    btnDisabled ||
                    isLoadingAll ||
                    (selectedProducts.length === 0 && isSelectAll === false)
                  }
                  transitionState={confirmButtonState}
                  color="primary"
                  variant="contained"
                  type="submit"
                  onClick={() => setStep("DiscountSubsidy")}
                >
                  <FormattedMessage
                    defaultMessage="Next"
                    description="button"
                  />
                </ConfirmButton>
              ) : (
                <ConfirmButton
                  idConfirmButton="assignProducts"
                  disabled={btnDisabled || isLoadingAll}
                  transitionState={confirmButtonState}
                  color="primary"
                  variant="contained"
                  type="submit"
                  onClick={submit}
                >
                  <FormattedMessage
                    defaultMessage="Assign Products"
                    description="button"
                  />
                </ConfirmButton>
              )}
            </DialogActions>
          </Dialog>
        );
      }}
    </Form>
  );
};
AssignProductSubsidyDialog.displayName = "AssignProductSubsidyDialog";
export default AssignProductSubsidyDialog;
